import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { usePreferences } from '../../lib/preferences'
import { useCurrentUser, isAdminRole, isSuperAdminRole } from '../../lib/auth'
import { useFeatureFlags } from '../../lib/features'
import { NAV_BY_ID, navLabel, type NavItemDef } from '../../lib/nav'
import { useMessages } from '@/i18n/messages'
import { shellMessages } from '@/i18n/text/shell'
import { Icon, NAV_ICON } from './Icon'
import { api, type AuthUser } from '../../lib/api'
import { WORK_BADGE_KEY } from '../../lib/work'

export function Sidebar() {
  const m = useMessages(shellMessages)
  const { data: user } = useCurrentUser()
  const flags = useFeatureFlags()
  const { sidebar, setOrder, toggleHidden } = usePreferences()
  const [editMode, setEditMode] = useState(false)
  const collapsed = sidebar.collapsed

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const orderedIds = useMemo(() => {
    return sidebar.order
      .map((id) => NAV_BY_ID[id])
      .filter((item): item is NavItemDef =>
        Boolean(item)
        && (!item.adminOnly || isAdminRole(user))
        && (!item.superAdminOnly || isSuperAdminRole(user))
        && (!item.featureFlag || flags[item.featureFlag]))
      .map((i) => i.id)
  }, [sidebar.order, user, flags])

  const visibleIds = editMode ? orderedIds : orderedIds.filter((id) => !sidebar.hidden.includes(id))

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const oldIndex = orderedIds.indexOf(String(active.id))
    const newIndex = orderedIds.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    setOrder(arrayMove(orderedIds, oldIndex, newIndex))
  }

  return (
    <aside
      className={`hidden md:flex flex-col flex-none bg-[var(--surface)] border-r border-[var(--border)] p-3 gap-1 overflow-hidden transition-[width] duration-200 ${
        collapsed ? 'w-[74px]' : 'w-[250px]'
      }`}
    >
      <nav className="flex flex-col gap-1">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={visibleIds} strategy={verticalListSortingStrategy}>
            {visibleIds.map((id) => (
              <SortableNavItem
                key={id}
                item={NAV_BY_ID[id]}
                label={id === 'customers' && !isAdminRole(user) ? m.myArea : navLabel(id)}
                editMode={editMode}
                collapsed={collapsed}
                hidden={sidebar.hidden.includes(id)}
                onToggleHidden={() => toggleHidden(id)}
              />
            ))}
          </SortableContext>
        </DndContext>
      </nav>

      <div className="flex-1" />

      {!collapsed && !editMode && <RoleCard user={user} />}

      {!collapsed && (
        <button
          onClick={() => setEditMode((v) => !v)}
          className={`mt-1 flex items-center gap-2 rounded-[11px] px-3 py-2 text-xs font-semibold transition ${
            editMode
              ? 'bg-[var(--brand)] text-[var(--on-brand)]'
              : 'text-[var(--muted)] hover:bg-[var(--surface-2)] hover:text-[var(--fg)]'
          }`}
        >
          <Icon name="edit" className="w-4 h-4" />
          {editMode ? m.doneCustomizing : m.customizeMenu}
        </button>
      )}
    </aside>
  )
}

function RoleCard({ user }: { user: AuthUser | null | undefined }) {
  const m = useMessages(shellMessages)
  const superadmin = isSuperAdminRole(user)
  const admin = isAdminRole(user)
  if (!admin) return null
  return (
    <div className="rounded-[14px] border border-[var(--border)] p-3.5 flex flex-col gap-2 bg-[linear-gradient(160deg,var(--brand-soft),transparent)]">
      <Icon name="premium" className="w-[22px] h-[22px] text-[var(--brand)]" />
      <span className="font-bold text-[13px] text-[var(--fg)]">
        {superadmin ? m.superadminAccess : m.adminAccess}
      </span>
      <span className="text-xs leading-relaxed text-[var(--muted)]">
        {superadmin
          ? m.superadminAccessBody
          : m.adminAccessBody}
      </span>
    </div>
  )
}

function WorkBadge({ dot = false }: { dot?: boolean }) {
  const { data } = useQuery({ queryKey: WORK_BADGE_KEY, queryFn: () => api.work.badge(), refetchInterval: 60_000, staleTime: 30_000 })
  const n = data?.count ?? 0
  if (n === 0) return null
  const title = `${n} ${n === 1 ? 'beiðni bíður' : 'beiðnir bíða'} svars frá þér`
  if (dot) return <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-[#dc2626] ring-2 ring-[var(--surface)]" title={title} />
  return (
    <span className="ml-auto inline-flex h-5 min-w-5 flex-none items-center justify-center rounded-full bg-[#dc2626] px-1.5 text-[11px] font-semibold text-white" title={title}>
      {n}
    </span>
  )
}

const navBase =
  'flex items-center gap-3 w-full rounded-[11px] px-3 py-2.5 text-sm font-semibold text-left transition text-[var(--fg-soft)] hover:bg-[var(--surface-2)] hover:text-[var(--fg)]'

function SortableNavItem({
  item,
  label,
  editMode,
  collapsed,
  hidden,
  onToggleHidden,
}: {
  item: NavItemDef
  label: string
  editMode: boolean
  collapsed: boolean
  hidden: boolean
  onToggleHidden: () => void
}) {
  const m = useMessages(shellMessages)
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: !editMode,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  }

  if (editMode) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="flex items-center gap-1.5 rounded-[11px] border border-[var(--border)] bg-[var(--surface-2)] px-2 py-2"
      >
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab touch-none text-[var(--faint)] hover:text-[var(--fg)]"
          title={m.dragToReorder}
          aria-label={m.dragToReorder}
        >
          <Icon name="grip" className="w-4 h-4" />
        </button>
        <span className={`flex flex-1 items-center gap-3 text-sm ${hidden ? 'text-[var(--faint)]' : 'text-[var(--fg-soft)]'}`}>
          <Icon name={NAV_ICON[item.id]} className="w-[20px] h-[20px]" />
          <span className="truncate">{label}</span>
        </span>
        <button
          onClick={onToggleHidden}
          className="text-[var(--faint)] hover:text-[var(--brand)]"
          title={hidden ? m.showInMenu : m.hideFromMenu}
          aria-label={hidden ? m.showInMenu : m.hideFromMenu}
        >
          <Icon name={hidden ? 'eye_off' : 'eye'} className="w-4 h-4" />
        </button>
      </div>
    )
  }

  return (
    <Link
      to={item.to}
      title={collapsed ? label : undefined}
      activeOptions={{ exact: item.to === '/' }}
      className={`${navBase} ${collapsed ? 'justify-center' : ''}`}
      activeProps={{
        style: { background: 'var(--brand-soft)', color: 'var(--brand)' },
      }}
    >
      {({ isActive }) => (
        <>
          <span className="relative flex-none">
            <Icon name={NAV_ICON[item.id]} className="w-[22px] h-[22px]" />
            {collapsed && item.id === 'verkefni' && <WorkBadge dot />}
          </span>
          {!collapsed && <span className="truncate">{label}</span>}
          {!collapsed && item.id === 'verkefni' && <WorkBadge />}
          {!collapsed && isActive && item.id !== 'verkefni' && (
            <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[var(--brand)]" />
          )}
        </>
      )}
    </Link>
  )
}
